import React, { useMemo } from 'react';
import 'styles/components/DefaultChartConfig.sass';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LegacyChart from 'interface/LegacyChart';
//import ProcessedChartData from 'interface/ProcessedChartData';
import processChartData from 'utils/processChartData';
import ZERONE_COLORS from 'constants/data/global/ZeroneColors';

interface BarChartProps {
    chart: LegacyChart;
    height?: number;
    className?: string;
}

/**
 * BarChart component renders a vertical bar chart using Recharts.
 * It processes the chart data and displays it with customizable axes, tooltips, and legends.
 *
 * @param {BarChartProps} props - The properties for the BarChart component.
 * @returns {JSX.Element} The rendered BarChart component.
 */
const BarChart: React.FC<BarChartProps> = ({ chart, height = 400, className = ''}) => {
    // Check if the chart has data
    const { processedData, hasLeftAxis, hasRightAxis, seriesConfig, formatAxisLabel, formatTooltipValue } = useMemo(() => processChartData(chart), [chart]);

    // If no chart data is available, return a placeholder
    if (!chart || !chart.series || chart.series.length === 0) {
        return <div className={`bar-chart ${className}`}>No data available</div>;
    }

    return (
        <div className='chart-container' style={{ height: `${height}px` }}>
            <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                    layout='vertical'
                    data={processedData}
                    margin={{ top: 20, right: hasRightAxis ? 30 : 20, left: 20, bottom: 60 }}
                    barCategoryGap={'20%'}
                >
                    <CartesianGrid
                        strokeDasharray={"3 3"}
                        stroke={ZERONE_COLORS.gridLines || '#CFE6F3'}
                        opacity={0.8}  
                    />

                    <YAxis
                        type="category"
                        dataKey="x"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                        label={{
                            value: chart.yAxisLabel || '',
                            angle: -90,
                            position: 'insideLeft',
                            offset: -10,
                            style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                        }}
                    />

                    {hasLeftAxis && (
                        <XAxis
                            xAxisId="left"
                            type="number"
                            orientation="bottom"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                            label={{
                            value: formatAxisLabel('left'),
                            position: 'insideBottom',
                            offset: -10,
                            style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                            }}
                        />
                        )}

                        {hasRightAxis && (
                        <XAxis
                            xAxisId="right"
                            type="number"
                            orientation="top"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                            label={{
                            value: formatAxisLabel('right'),
                            position: 'insideTop',
                            offset: 10,
                            style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                            }}
                        />
                        )}

                    <Tooltip
                        contentStyle={{
                            backgroundColor: ZERONE_COLORS.background,
                            borderColor: `1px solid ${ZERONE_COLORS.border}`,
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            padding: '10px',
                            fontSize: '14px'
                        }}
                        labelStyle={{
                            color: ZERONE_COLORS.textPrimary,
                            fontWeight: 600
                        }}
                        formatter={formatTooltipValue}
                    />

                    <Legend
                        wrapperStyle={{
                            paddingTop: '10px',
                            fontSize: '14px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            color: ZERONE_COLORS.textPrimary
                        }}
                        iconType="square"
                        iconSize={12}
                    />

                    {seriesConfig.map((series) => (
                        <Bar
                            key={series.dataKey}
                            dataKey={series.dataKey}
                            fill={series.color}
                            name={series.name}
                            xAxisId={series.axis}
                            radius={[2, 2, 0, 0]} // Rounded corners for the top of the bars
                            animationDuration={800}
                            animationEasing='ease-out'
                        />
                    ))}

                </RechartsBarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default BarChart;
